<script lang="ts">
    import RewardsCopyField from "@apps/rewards/components/Inputs/RewardsCopyField.svelte";
    import RewardsCustomerStatusBasedContent from "@apps/rewards/components/RewardsCustomerStatusBasedContent.svelte";
    import RewardsReferralShare from "@apps/rewards/components/RewardsReferral/RewardsReferralShare.svelte";
    import RewardsWidgetReward from "@apps/rewards/components/RewardsWidgetReward/RewardsWidgetReward.svelte";
    import type { RewardsTranslationNamespaces } from "@apps/rewards/constants/constants";
    import type RewardsAmountFormatter from "@apps/rewards/lib/RewardsAmountFormatter/RewardsAmountFormatter";
    import type RewardsCurrencyFormatter from "@apps/rewards/lib/RewardsCurrencyFormatter";
    import type { RewardsReferralProgramEntity } from "@apps/rewards/modules/ReferralProgram/models/RewardsReferralProgramEntities";
    import type { REWARDS_REFERRAL_SHARE_CHANNELS_TYPE } from "@apps/rewards/modules/ReferralProgram/types";
    import type { RewardsStorefrontValuesSettingsEntity } from "@apps/rewards/modules/StorefrontValues/models/RewardsStorefrontValuesEntities";
    import type { DesignModeProviderInterface } from "@modules/common_module/DesignModeProvider/DesignModeProviderInterface";
    import type { URLsBuilder } from "@modules/common_module/URLsBuilder/URLsBuilder";
    import type { GwI18n } from "@modules/i18n/GwI18n/GwI18n";
    import type HTMLSanitizerInterface from "@modules/money/HTMLSanitizer/HTMLSanitizerInterface";

    import T from "@components/T.svelte";
    import Text from "@components/Typography/Text.svelte";

    import RewardsDesignModeGuard from "../RewardsDesignModeGuard.svelte";
    import RewardsPointsForAmountReward from "../RewardsWidgetReward/RewardsPointsForAmountReward.svelte";

    //Props
    export let isCustomer: boolean;
    export let referralProgram: RewardsReferralProgramEntity;
    export let referralLink: string;
    export let receiverMailToLink: string;
    export let referralShareChannels: RewardsStorefrontValuesSettingsEntity["referralShareChannels"];
    export let amountFormatter: RewardsAmountFormatter;
    export let currencyFormatter: RewardsCurrencyFormatter;
    export let urlsBuilder: URLsBuilder;
    export let i18n: GwI18n;
    export let namespace: RewardsTranslationNamespaces;
    export let pointsNamespace: RewardsTranslationNamespaces;
    export let sanitizer: HTMLSanitizerInterface;
    export let designModeProvider: DesignModeProviderInterface;
    export let onWindowOpen: (
        channelType: REWARDS_REFERRAL_SHARE_CHANNELS_TYPE
    ) => void;
</script>

<div class="gw-rd-popup-widget-referral-program">
    {#if referralProgram?.prizeForSender.rewarding}
        {@const reward = referralProgram.prizeForSender.rewarding}
        <div class="gw-rd-popup-widget-referral-program__reward">
            <Text component="span" variant="secondary" size="xs">
                <T
                    {namespace}
                    translationKey="referral_program_reward_you_get"
                />
            </Text>
            <Text variant="primary" size="xs" weight="semibold" compact>
                {#if "spendAmountDivider" in reward}
                    <RewardsPointsForAmountReward
                        points={reward.points}
                        amount={reward.spendAmountDivider}
                        {amountFormatter}
                        {currencyFormatter}
                        {sanitizer}
                        {i18n}
                        {namespace}
                        {pointsNamespace}
                    />
                {:else}
                    <RewardsWidgetReward
                        {reward}
                        {i18n}
                        {currencyFormatter}
                        {amountFormatter}
                        {namespace}
                        {pointsNamespace}
                        {urlsBuilder}
                        {sanitizer}
                    />
                {/if}
            </Text>
        </div>
    {/if}
    {#if referralProgram?.prizeForReceiver.rewarding}
        <div class="gw-rd-popup-widget-referral-program__reward">
            <Text component="span" variant="secondary" size="xs">
                <T
                    {namespace}
                    translationKey="referral_program_reward_they_get"
                />
            </Text>
            <Text variant="primary" size="xs" weight="semibold" compact>
                <RewardsWidgetReward
                    reward={referralProgram.prizeForReceiver.rewarding}
                    {urlsBuilder}
                    {i18n}
                    {currencyFormatter}
                    {amountFormatter}
                    {namespace}
                    {pointsNamespace}
                    {sanitizer}
                />
            </Text>
        </div>
    {/if}
</div>
<RewardsDesignModeGuard {designModeProvider}>
    <RewardsCustomerStatusBasedContent isCustomerContent={isCustomer}>
        <div
            slot="customer-content"
            class="gw-rd-popup-widget-referral-program-share"
        >
            <RewardsCopyField
                value={referralLink}
                {namespace}
                ariaLabel="Copy referral link to clipboard"
            />
            <RewardsReferralShare
                {referralShareChannels}
                {receiverMailToLink}
                {namespace}
                {onWindowOpen}
            />
        </div>
    </RewardsCustomerStatusBasedContent>
</RewardsDesignModeGuard>

<style lang="postcss">
    .gw-rd-popup-widget-referral-program {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: var(--gw-rd-space-2);

        &__reward {
            display: grid;
            grid-auto-rows: max-content;
            padding: var(--gw-rd-space-3) var(--gw-rd-space-4);
            border: 1px solid var(--gw-rd-field-border-color);
            border-radius: var(--gw-rd-border-radius-xs);
        }
    }

    .gw-rd-popup-widget-referral-program-share {
        display: grid;
        gap: var(--gw-rd-space-6);
    }
</style>
